import React from "react";
import { FormControl, FormLabel, Select, Option, Typography, SelectProps } from "@mui/joy";
import { ChevronDown } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";

interface OptionType {
    value: string;
    label: string;
}

interface FormSelectProps extends Omit<SelectProps<any, false>, 'size'> {
    name: string;
    label?: string;
    helperText?: string;
    options: { label: string; value: string | number }[] | string[];
    startDecorator?: React.ReactNode;
    onValueChange?: (value: any) => void;
    size?: "sm" | "md" | "lg";
    [key: string]: any;
}

export default function FormSelect({
    name,
    label,
    helperText,
    options,
    startDecorator,
    onValueChange,
    size = "md",
    ...props
}: FormSelectProps) {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
                <FormControl size={size} error={!!fieldState.error}>
                    {label && (
                        <FormLabel sx={{ textTransform: "uppercase", fontSize: "12px", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            {label}
                        </FormLabel>
                    )}
                    {helperText && (
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            {helperText}
                        </Typography>
                    )}
                    <Select
                        {...field}
                        size={size}
                        startDecorator={startDecorator}
                        indicator={<ChevronDown size={16} />}
                        sx={{ bgcolor: "background.surface", borderRadius: "4px", boxShadow: "none" }}
                        {...props}
                        onChange={(_, newValue) => {
                            field.onChange(newValue);
                            onValueChange?.(newValue);
                        }}
                    >
                        {options.map((opt: any) => {
                            if (typeof opt === "string") {
                                return <Option key={opt} value={opt}>{opt}</Option>;
                            }
                            return <Option key={opt.value} value={opt.value}>{opt.label}</Option>;
                        })}
                    </Select>
                    {fieldState.error && (
                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                            {fieldState.error.message}
                        </Typography>
                    )}
                </FormControl>
            )}
        />
    );
}
